import { useCallback, useEffect, useMemo } from 'react';
import useTrade from 'views/Conversation/Sider/useTrade';
import type { RefundDrawerDataSource } from '../types';
import { toUpperCase } from 'utils/toLowerCase';

interface UseRefundDrawerTradeParams {
  dataSource: RefundDrawerDataSource | null;
  visible: boolean;
  platform: General.Platform;
  conversation?: Conversation.Conversation;
}

/**
 * 管理退款抽屉内订单（Trade）的拉取与当前匹配的 trade 实例。
 * 当抽屉可见时按 dataSource 拉取订单列表，并解析出当前 returnSn 对应的 trade。
 */
export function useRefundDrawerTrade({
  dataSource,
  visible,
  platform,
  conversation,
}: UseRefundDrawerTradeParams) {
  const internalStoreId = dataSource?.internalStoreId ?? '';
  const useTradeResult = useTrade(internalStoreId);
  const { fetchList, refreshTrade } = useTradeResult;

  const pullTrade = useCallback(
    (refresh = false) => {
      if (!visible || !dataSource) return;

      const { externalBuyerId: buyerId, internalStoreId: storeId, orderSn: tradeId } = dataSource;

      const params = {
        page_no: 1,
        page_size: 1,
        external_trade_ids: tradeId,
        external_buyer_id: buyerId,
        tunnelAccountId: conversation?.tunnelAccountId || '0',
        internal_store_id: storeId,
        filter_canceled_refund: false,
      };

      if (refresh) {
        refreshTrade(params, toUpperCase(platform));
        return;
      }
      fetchList(params, toUpperCase(platform));
    },
    [dataSource, fetchList, platform, refreshTrade, visible, conversation?.tunnelAccountId],
  );

  useEffect(() => {
    pullTrade();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource?.internalTradeId]);

  const trade = useMemo(() => {
    if (!dataSource) return undefined;
    const { list } = useTradeResult;
    return list.find((t) => t.externalTradeId === dataSource.orderSn);
  }, [dataSource, useTradeResult]);

  return { trade, pullTrade, useTradeResult };
}
